import React, { useState } from 'react';

import { APButton } from '@astropay/web-core-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  CircularProgress,
  createStyles,
  Dialog,
  DialogContent,
  DialogContentText,
  Link,
  makeStyles,
  Slide,
  Theme,
} from '@material-ui/core';
import { TransitionProps } from '@material-ui/core/transitions';

import EventNotification from 'common/services/event-notification';

import { translate } from 'common/config/i18n';
import { useParentPostMessage } from 'common/helpers/hooks';
import { getQueryParam } from 'common/helpers/url';

import CloseSdkAction from '../close-sdk-action/close-sdk-action';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1100,
    },
    iconContainer: {
      marginBottom: theme.spacing(2),
    },
    icon: {
      fontSize: '2em',
    },
    iconHover: {
      fontSize: '2em',
      backgroundColor: '#fff',
      borderRadius: '100%',
      marginLeft: '-40px',
      marginBottom: '-5px',
    },
    description: {
      textAlign: 'center',
      maxWidth: '400px',
      color: theme.palette.text.primary,
      padding: theme.spacing(0, 2),
    },
    link: { marginTop: theme.spacing(2), '& a:': { color: theme.colors.Text_1 } },
  }),
);

const Transition = React.forwardRef(function Transition(
  // eslint-disable-next-line
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export function handleMessageEvent(event: MessageEvent) {
  const eventNotification = new EventNotification('*', window);
  const triggerEvent = event.data?.action.includes('ap') || event.data?.action === 'close';

  if (event.origin !== process.env.REACT_APP_OT_BASE_URL) {
    return;
  }

  if (triggerEvent) {
    eventNotification.notify('close', {});
    useParentPostMessage(parent, event.data, false);
    window.removeEventListener('message', handleMessageEvent);
  }
}

const CookiesValidation: React.FC = () => {
  const urlToRedirect = window.self.location.href;
  const classes = useStyles();
  const isSdk = !!getQueryParam('condensed');
  const isIframe = window.self !== window.top;

  const [redirected, setRedirected] = useState(false);

  const setListener = () => {
    window.addEventListener('message', handleMessageEvent);
  };

  const onRedirectAction = () => {
    setRedirected(true);
    setListener();
  };

  const content = () => {
    if (!redirected) {
      return (
        <DialogContent className={classes.root}>
          <Box className={classes.iconContainer}>
            <FontAwesomeIcon className={classes.icon} icon={['far', 'cookie']} size="lg" />
            <FontAwesomeIcon className={classes.iconHover} icon={['far', 'cookie']} size="lg" />
          </Box>
          <DialogContentText id="alert-dialog-description" className={classes.description}>
            {isIframe
              ? translate('one_touch_cookies_blocked', {
                  button: translate('deposit_continue'),
                })
              : translate('deposit_allow_cookies')}
          </DialogContentText>
          {isIframe ? (
            <APButton
              id={'reload-btn'}
              className={classes.link}
              variant="text"
              onClick={() => setListener()}
            >
              <Link href={urlToRedirect} target="AstroPay" onClick={onRedirectAction}>
                {translate('deposit_continue')}
              </Link>
            </APButton>
          ) : (
            <APButton
              className={classes.link}
              id={'reload-btn'}
              variant="text"
              onClick={() => window.location.reload()}
            >
              {translate('deposit_continue')}
            </APButton>
          )}
        </DialogContent>
      );
    }

    return (
      <DialogContent className={classes.root}>
        <Box className={classes.iconContainer}>
          <CircularProgress />
        </Box>
        <DialogContentText id="alert-dialog-description" className={classes.description}>
          {translate('deposit_waiting_payment')}
        </DialogContentText>
      </DialogContent>
    );
  };

  return (
    <>
      <Dialog
        open
        TransitionComponent={Transition}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        {content()}
      </Dialog>
      {((isIframe && isSdk) || (isIframe && redirected)) && <CloseSdkAction />}
    </>
  );
};

export default CookiesValidation;
